import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadSimple, Lightning, ArrowsLeftRight, FileAudio, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

export default function Dashboard() {
  const [direction, setDirection] = useState("hi-en");
  const [file, setFile] = useState(null);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => { api.get("/runs").then(({ data }) => setRecent(data.slice(0, 5))).catch(() => {}); }, []);

  const pick = (f) => {
    if (!f) return;
    if (!/\.(wav|mp3|m4a|ogg|flac)$/i.test(f.name)) { toast.error("Unsupported format. Use wav / mp3."); return; }
    setFile(f); setRun(null);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("direction", direction);
      const { data } = await api.post("/runs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRun(data);
      toast.success("Clip uploaded. Ready to dub.");
    } catch (e) { toast.error("Upload failed."); }
    setBusy(false);
  };

  const dub = async () => {
    if (!run) return;
    setBusy(true);
    try {
      await api.post(`/runs/${run.id}/start`);
      nav(`/runs/${run.id}`);
    } catch { toast.error("Could not start pipeline."); setBusy(false); }
  };

  return (
    <div>
      <div className="overline mb-2">new dubbing run</div>
      <h1 className="font-mono font-extrabold tracking-tighter text-3xl mb-8">Upload &amp; orchestrate</h1>

      <div className="grid lg:grid-cols-3 gap-px bg-border border border-border mb-10">
        {/* upload */}
        <div className="lg:col-span-2 bg-[#18181B] p-6">
          <div className="overline mb-4">1 · source clip</div>
          <div
            data-testid="upload-dropzone"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
            className={`border border-dashed p-10 flex flex-col items-center justify-center cursor-pointer transition-colors ${drag ? "border-primary bg-[#1f1a15]" : "border-border hover:border-muted-foreground"}`}>
            <input ref={inputRef} data-testid="upload-audio-input" type="file" accept=".wav,.mp3,.m4a,.ogg,.flac,audio/*" className="hidden"
              onChange={(e) => pick(e.target.files?.[0])} />
            {file ? (
              <div className="flex items-center gap-3 font-mono text-sm">
                <FileAudio size={28} className="text-primary" weight="fill" />
                <div><div>{file.name}</div><div className="text-muted-foreground text-xs">{(file.size / 1024 / 1024).toFixed(2)} MB</div></div>
              </div>
            ) : (
              <>
                <UploadSimple size={32} className="text-muted-foreground mb-3" />
                <div className="font-mono text-sm">drop audio or click to browse</div>
                <div className="overline mt-2" style={{ fontSize: "0.55rem" }}>wav · mp3 · up to ~10 min</div>
              </>
            )}
          </div>
        </div>

        {/* direction + actions */}
        <div className="bg-[#18181B] p-6 flex flex-col">
          <div className="overline mb-4">2 · direction</div>
          <div className="grid grid-cols-1 gap-px bg-border border border-border mb-4">
            {[["hi-en", "Hindi", "English", "default"], ["en-hi", "English", "Hindi", "optional"]].map(([val, src, tgt, tag]) => (
              <button key={val} data-testid={`direction-${val}`} onClick={() => setDirection(val)}
                className={`flex items-center justify-between p-4 font-mono text-sm transition-colors ${direction === val ? "bg-primary text-black" : "bg-[#18181B] text-foreground hover:bg-secondary"}`}>
                <span className="flex items-center gap-2">{src} <ArrowsLeftRight size={14} /> {tgt}</span>
                <span className="overline" style={{ fontSize: "0.5rem", color: direction === val ? "#000" : undefined }}>{tag}</span>
              </button>
            ))}
          </div>
          <div className="mt-auto space-y-2">
            {!run ? (
              <button data-testid="upload-submit-button" disabled={!file || busy} onClick={upload}
                className="w-full bg-secondary text-foreground font-mono py-3 flex items-center justify-center gap-2 hover:bg-[#27272a] transition-colors disabled:opacity-40">
                {busy ? "uploading…" : "upload clip"}
              </button>
            ) : (
              <button data-testid="dub-submit-button" disabled={busy} onClick={dub}
                className="w-full bg-primary text-black font-mono font-bold py-3 flex items-center justify-center gap-2 hover:bg-[#FF7733] hover:-translate-y-[1px] transition-transform disabled:opacity-40">
                <Lightning weight="fill" size={18} /> {busy ? "starting…" : "Dub it"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* recent */}
      <div className="flex items-center justify-between mb-4">
        <div className="overline">recent runs</div>
        <button onClick={() => nav("/history")} className="font-mono text-xs text-primary flex items-center gap-1 hover:gap-2 transition-all">all runs <ArrowRight size={12} /></button>
      </div>
      <div className="border border-border">
        {recent.length === 0 && <div className="p-6 font-mono text-sm text-muted-foreground bg-[#18181B]">No runs yet. Upload a clip to begin.</div>}
        {recent.map((r) => (
          <button key={r.id} data-testid={`recent-run-${r.id}`} onClick={() => nav(`/runs/${r.id}`)}
            className="w-full flex items-center justify-between p-4 border-b border-border last:border-b-0 bg-[#18181B] hover:bg-secondary transition-colors text-left">
            <div className="flex items-center gap-3 min-w-0">
              <FileAudio size={18} className="text-muted-foreground shrink-0" />
              <span className="font-mono text-sm truncate">{r.original_filename}</span>
              <span className="overline shrink-0" style={{ fontSize: "0.5rem" }}>{r.direction}</span>
            </div>
            <span className="font-mono text-xs shrink-0" style={{ color: r.status === "completed" ? "#00E559" : r.status === "failed" ? "#FF3333" : "#A1A1AA" }}>{r.status}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
